import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  HiOutlineClipboardList,
  HiOutlineDocumentText,
  HiOutlineHome,
  HiOutlineLogout,
  HiOutlineNewspaper,
} from "react-icons/hi";
import { useAuth } from "../../context/AuthContext";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: HiOutlineHome, end: true },
  { to: "/dashboard/pages", label: "Pages", icon: HiOutlineDocumentText },
  { to: "/dashboard/blog", label: "Blog", icon: HiOutlineNewspaper },
  { to: "/dashboard/cases", label: "Cases", icon: HiOutlineClipboardList },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
          COI CMS
        </p>
        <h1 className="mt-1 text-lg font-semibold text-slate-900">Admin Dashboard</h1>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition",
                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                ].join(" ")
              }
            >
              <Icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-slate-200 p-4">
        <div className="mb-3 rounded-2xl bg-slate-50 px-3 py-3">
          <p className="truncate text-sm font-semibold text-slate-900">
            {user?.displayName || user?.username || "Administrator"}
          </p>
          <p className="truncate text-xs text-slate-500">
            {(user?.roles || []).join(", ") || "Signed in"}
          </p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          <HiOutlineLogout className="h-5 w-5" />
          Log out
        </button>
      </div>
    </aside>
  );
};

const Topbar = ({ title, subtitle }) => (
  <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur">
    <h2 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
    {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
  </header>
);

const DashboardLayout = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen bg-slate-50 font-inter text-slate-900" dir="ltr">
    <Sidebar />
    <div className="flex min-w-0 flex-1 flex-col">
      <Topbar title={title} subtitle={subtitle} />
      <main className="flex-1 p-6">{children || <Outlet />}</main>
    </div>
  </div>
);

export default DashboardLayout;
export { Sidebar, Topbar };
